import { routes } from '../assessment/routes';

/**
 * Shell resolution for the two-shell composition (V2-018C-1).
 *
 * The application composed a single shell for every route: one narrow centred
 * region with a text-wordmark header and a legal-links footer. That shape is
 * correct for an application and wrong for a public website, which needs a wider
 * header, full-width section bands, and a richer footer. The two shells are the
 * `ExperienceShell` (public website) and the `ApplicationShell` (every other
 * route); `public` and `app` below are the internal classification values that
 * select between them.
 *
 * Shell selection is a pure function of the path so it can be asserted directly,
 * without mounting anything. A route that is not public gets the Application
 * Shell, which makes the fallback safe: an unrecognised path renders the shell
 * that carries no marketing navigation.
 */
export type ShellKind = 'public' | 'app';

/**
 * The public surface, per section 1.1 of the approved V2-018C specification.
 *
 * The three assessment marketing paths are listed here before their pages
 * exist. That is deliberate: this slice delivers the shell and its navigation,
 * and navigation needs destinations. Until the page slices land, these paths
 * render the not-found fallback inside the Experience Shell.
 */
export const PUBLIC_PATHS: readonly string[] = [
  routes.home,
  routes.why,
  routes.bottleneck,
  routes.workFit,
  routes.perspective,
  routes.privacy,
  routes.terms,
];

/**
 * Normalise before comparison so `/why/` and `/why` resolve alike. The root is
 * exempt: stripping its slash would leave an empty string.
 */
function normalise(path: string): string {
  const withoutTrailing = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return withoutTrailing === '' ? '/' : withoutTrailing;
}

export function resolveShell(path: string): ShellKind {
  return PUBLIC_PATHS.includes(normalise(path)) ? 'public' : 'app';
}

/**
 * Content measure within the Application Shell (V1 admin visual milestone).
 *
 * Every application route used one narrow measure, which is right for reading a
 * report and wrong for an operations console: the approved admin design places
 * paired health readings side by side, and at `max-w-3xl` they stack into a
 * column that reads like a list of unrelated numbers. The Product Owner
 * approved the wider layout for the console specifically.
 *
 * Kept as a pure function of the path, for the same reason `resolveShell` is:
 * it can be asserted without mounting anything, and a new admin route cannot
 * quietly inherit the wrong measure.
 */
export type ShellWidth = 'default' | 'wide';

export function resolveShellWidth(path: string): ShellWidth {
  // Exact, not prefixed. `/admin` is the only admin route the application
  // serves — every sibling renders Not Found (spec §1) — and a not-found page
  // has no reason to be laid out as a console.
  return normalise(path) === routes.admin ? 'wide' : 'default';
}
